import request from 'superagent';
import {log} from '../api/logger';
import getCsrfToken from './csrfTokenHelper';

const TIMEOUT_LENGTH = 15000;
const CSRF_HEADER_NAME = 'x-csrf-jwt-pdp';
const ACCESS_TOKEN_TIMEOUT = 30000;

const createAccessTokenPromise = () => {
    // create a promise that will be resolved after the `window.ha.accessTokenCallbacks` have been triggered
    // or rejected after `ACCESS_TOKEN_TIMEOUT` has elapsed
    let accessTokenPromise;

    global.window.ha = global.window.ha || {};
    global.window.ha.accessTokenCallbacks = global.window.ha.accessTokenCallbacks || [];

    // create the access token promise
    accessTokenPromise = new Promise((resolve, reject) => {
        global.window.ha.accessTokenCallbacks.push((successful, error) => {
            if (successful) {
                resolve();
            } else {
                reject(error);
            }
        });
    });

    // create the timeout promise
    let timeoutId;
    const timeoutPromise = new Promise((resolve, reject) => {
        timeoutId = setTimeout(
            () => reject(new Error('Access token request timed out.')),
            ACCESS_TOKEN_TIMEOUT
        );
    });

    // create a wrapper around both promises
    accessTokenPromise = Promise.race([accessTokenPromise, timeoutPromise]);

    // clean up our timeouts after the wrapper has been resolved/rejected
    accessTokenPromise.then(() => clearTimeout(timeoutId)).catch(() => clearTimeout(timeoutId));
    return accessTokenPromise;
};

/**
 * Provides a "safe" way for retrieving the CSRF token where an empty string is returned in the case where the underlying
 * CSRF token request has failed.  We use this to allow AJAX requests to proceed even when we don't have a valid CSRF token.
 * @returns {Promise<string>}
 */
async function getCsrfTokenSafe() {
    try {
        return await getCsrfToken();
    } catch (error) {
        return '';
    }
}

/**
 * Returns a promise that will be resolved after the `window.ha.accessTokenCallbacks` have been triggered
 * or rejected after ACCESS_TOKEN_TIMEOUT has elapsed.
 * @returns {Promise<*>}
 */
async function waitForAccessTokenSafe() {
    try {
        return await createAccessTokenPromise();
    } catch (error) {
        log({
            message: `Access token request failed: ${error.message}`,
            grecaptchaScriptTimings: window.recaptchaScriptTimestamps
        });

        return false;
    }
}

async function ajax(url, data, waitForAccessToken) {
    if (waitForAccessToken) {
        await waitForAccessTokenSafe();
    }

    const csrfToken = await getCsrfTokenSafe();
    return new Promise((resolve, reject) => {
        request
            .get(url)
            .set(CSRF_HEADER_NAME, csrfToken)
            .send(data || {})
            .end((error, response) => {
                if (error) {
                    reject(error);
                } else {
                    resolve(response);
                }
            });
    });
}

async function get(url, data, waitForAccessToken) {
    if (waitForAccessToken) {
        await waitForAccessTokenSafe();
    }

    const csrfToken = await getCsrfTokenSafe();
    const req = request.get(url).set(CSRF_HEADER_NAME, csrfToken);

    if (data) {
        req.query(data);
    }

    // Wrap superagent into a standard promise.
    const promise = new Promise((resolve, reject) => {
        req.timeout({
            response: TIMEOUT_LENGTH
        }).end((error, response) => {
            if (error) {
                reject(error);
            } else {
                resolve(response);
            }
        });
    });

    // Passthrough the abort to the superagent abort.
    promise.abort = () => {
        req.abort();
    };

    return promise;
}

async function post(url, data, headers = {}, waitForAccessToken) {
    if (waitForAccessToken) {
        await waitForAccessTokenSafe();
    }

    const csrfToken = await getCsrfTokenSafe();

    return new Promise((resolve, reject) => {
        const initialRequest = request.post(url).set(CSRF_HEADER_NAME, csrfToken);

        Object.keys(headers).forEach((headerName) => {
            initialRequest.set(headerName, headers[headerName]);
        });

        initialRequest.send(data).end((error, response) => {
            if (error) {
                log({
                    postAjaxHelperReject: true,
                    url,
                    error: error.message || ''
                });
                reject(error);
            } else {
                resolve(response);
            }
        });
    });
}

export default ajax;
export {CSRF_HEADER_NAME, createAccessTokenPromise, ajax, get, post};
